import React, { useContext, useRef, useState, useEffect } from "react";
import KRGlue from "@lyracom/embedded-form-glue";
import {
  postCreateUpdatePaymentMethodForm,
  getUpdatePaymentMethodCallback,
  PAYMENT_GATEWAY_PUBLIC_KEY,
} from "../../core/api/Api";
import { useTranslation } from "react-i18next";
import { showToast } from "../../core/common-service/CommonService";
import useLangNavigate from "../common/useLangNavigate";
import { PAYMENT_GATEWAY_ENDPOINT } from "../../core/constants/constants";
import { StoreContext } from "../../core/context/StoreContext";
import "./UpdatePaymentMethod.css";

const UpdatePaymentMethod = () => {
  const { triggerSubscriptionRefresh } = useContext(StoreContext);
  const { t, i18n } = useTranslation();
  const navigate = useLangNavigate();
  const [message, setMessage] = useState("");
  const [isReactivation, setIsReactivation] = useState(false);
  const onInit = useRef(true);
  const mounted = useRef(true);

  useEffect(() => {
    if (!onInit.current) return;
    onInit.current = false;
    window.scroll(0, 0);

    async function setupPaymentForm() {
      try {
        const res = await postCreateUpdatePaymentMethodForm();
        const response = res?.data?.success;
        if (!response) return;

        const formToken = response.formToken;
        const orderId = response.order_id;
        const reactivation = response.is_reactivation || false;
        if (mounted.current) setIsReactivation(reactivation);

        const { KR } = await KRGlue.loadLibrary(
          PAYMENT_GATEWAY_ENDPOINT,
          PAYMENT_GATEWAY_PUBLIC_KEY
        );

        await KR.setFormConfig({
          formToken,
          "kr-language": i18n.language,
        });

        await KR.renderElements("#myPaymentForm");

        await KR.onSubmit(async (paymentRes) => {
          const orderStatus = paymentRes.clientAnswer.orderStatus;
          if (orderStatus === "PAID" || orderStatus === "ACCEPTED") {
            try {
              const callbackRes = await getUpdatePaymentMethodCallback(orderId);
              const success = callbackRes?.data?.success;

              if (success) {
                triggerSubscriptionRefresh();
                showToast({
                  type: "success",
                  message: success.is_reactivation
                    ? t("subscribe.subscription_reactivated")
                    : t("subscribe.card_updated_successfully"),
                });
                navigate("/payment-methods");
              } else {
                showToast({
                  type: "error",
                  message: t("subscribe.card_update_error"),
                });
              }
            } catch (err) {
              showToast({
                type: "error",
                message:
                  err?.response?.data?.error || t("subscribe.card_update_error"),
              });
            }
          }
          return false;
        });

        await KR.onError(async (errRes) => {
          showToast({
            type: "error",
            message:
              errRes.detailedErrorMessage || t("subscribe.card_update_error"),
          });
        });
      } catch (error) {
        setMessage(String(error));
      }
    }

    setupPaymentForm();
    return () => {
      mounted.current = false;
    };
  }, [i18n.language, navigate, t, triggerSubscriptionRefresh]);

  return (
    <section className="update-payment-method-section py-100">
      <div className="container">
        <div className="update-payment-wrapper">
          <h2 className="update-payment-title">
            {isReactivation
              ? t("subscribe.reactivate_subscription_title")
              : t("subscribe.card_update_title")}
          </h2>
          <p className="update-payment-description">
            {isReactivation
              ? t("subscribe.reactivate_description")
              : t("subscribe.card_update_description")}
          </p>

          <div className="form">
            <div className="d-flex align-items-center justify-content-center">
              <div id="myPaymentForm">
                <div
                  className="kr-smart-form"
                  kr-card-form-expanded="true"
                ></div>
              </div>
              <div data-test="payment-message">{message}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UpdatePaymentMethod;
